import { useCallback, useEffect, useState } from 'react';
import { CalendarCheck, CalendarClock, ChevronLeft, ChevronRight, Repeat } from 'lucide-react';
import { SHIFT_SHORT, addDays, fmtWeekday, mondayOf, type Shift } from '@siva/shared';
import { api, isUnreachable, post } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { LAST_OFF_DAYS, recall, remember } from '../../lib/offline.js';
import { OfflineNote } from '../../lib/OfflineNote.js';
import { errorMessage } from '../../lib/passkey.js';
import { Alert, fmtDate, Spinner } from '../../lib/ui.js';
import { SlideOver } from '../../lib/SlideOver.js';
import { DateField } from '../../lib/DateField.js';
import { t } from '../../i18n/index.js';

/** One day of a published week, and who is off on it. Empty for a week still in draft. */
type PlanDay = { date: string; workers: { id: string; name: string; shift: Shift }[] };
type WeekInfo = {
  week_start: string;
  published: boolean;
  off_date: string | null;
  days?: PlanDay[];
};

type SwapState =
  | 'waiting_colleague'
  | 'pending_admin'
  | 'agreed_pending_admin'
  | 'declined'
  | 'cancelled'
  | 'approved'
  | 'rejected';

type SwapRequest = {
  id: string;
  mine: boolean;
  worker: { id: string; name: string };
  counterparty: { id: string; name: string } | null;
  from_date: string;
  to_date: string;
  reason: string | null;
  state: SwapState;
  decision_note: string | null;
  requested_at: string;
  decided_at: string | null;
};

const dayName = (ymd: string) => fmtWeekday(ymd, 'long');

/** One sentence for where a request stands, from the point of view of whoever is reading. */
function stateLine(r: SwapRequest): string {
  const other = r.counterparty?.name ?? '';
  switch (r.state) {
    case 'waiting_colleague':
      return r.mine ? t('swap_state_waiting', { name: other }) : t('swap_state_asked_you');
    case 'pending_admin':
    case 'agreed_pending_admin':
      return t('swap_state_with_admin');
    case 'approved':
      return t('swap_state_approved');
    case 'rejected':
      return t('swap_state_rejected');
    case 'declined':
      return r.mine ? t('swap_state_declined', { name: other }) : t('swap_state_you_declined');
    case 'cancelled':
      return t('swap_state_cancelled');
  }
}

const stateTone = (s: SwapState) =>
  s === 'approved'
    ? 'good'
    : s === 'rejected' || s === 'declined' || s === 'cancelled'
      ? 'bad'
      : 'warn';

/**
 * The worker's off days, one week at a time — any past week, this week, next week and the one
 * after once published — as the seven days of that week with the off day marked, and the button
 * to ask for it to be moved. It was three
 * cards each saying "Tuesday · 22/09/2026" in large type, which the owner called a simple page;
 * seeing the day in its week is what a calendar is for.
 */
export function OffDaysPage() {
  // Their own id, to pick their name out of the week's roster below.
  const { me } = useAuth();
  const meId = me?.user?.id ?? '';
  const [weeks, setWeeks] = useState<WeekInfo[] | null>(null);
  const [requests, setRequests] = useState<SwapRequest[]>([]);
  const [today, setToday] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [asking, setAsking] = useState<WeekInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [knownAt, setKnownAt] = useState<string | null>(null);
  /** The Monday being looked at; null is this week. */
  const [shown, setShown] = useState<string | null>(null);
  /** Weeks fetched on request — past ones — kept so stepping back and forth asks once each. */
  const [older, setOlder] = useState<Record<string, WeekInfo>>({});

  useEffect(() => {
    if (!shown || !weeks || weeks.some((w) => w.week_start === shown) || older[shown]) return;
    let live = true;
    api<{ weeks: WeekInfo[] }>(`/me/off-days?week=${shown}`)
      .then((r) => {
        const got = r.weeks[0];
        if (live && got) setOlder((o) => ({ ...o, [shown]: got }));
      })
      .catch((err: unknown) => {
        if (live) setError(errorMessage(err));
      });
    return () => {
      live = false;
    };
  }, [shown, weeks, older]);

  const load = useCallback(async () => {
    try {
      const [w, s] = await Promise.all([
        api<{ weeks: WeekInfo[] }>('/me/off-days'),
        api<{ requests: SwapRequest[]; open_request_id: string | null; today: string }>(
          '/me/off-swaps',
        ),
      ]);
      setWeeks(w.weeks);
      setRequests(s.requests);
      setOpenId(s.open_request_id);
      setToday(s.today);
      remember(LAST_OFF_DAYS, { weeks: w.weeks, today: s.today });
      setKnownAt(null);
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
      // Which day they are off next week is worth knowing at the gate. Swap requests are not
      // remembered: answering one needs the server, so showing a stale one would only mislead.
      if (!isUnreachable(err)) return;
      setWeeks((current) => {
        if (current) return current;
        const last = recall<{ weeks: WeekInfo[]; today: string }>(LAST_OFF_DAYS);
        if (!last) return current;
        setToday(last.value.today);
        setKnownAt(last.at);
        return last.value.weeks;
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (error && !weeks) return <OfflineNote at={null} onRetry={() => void load()} />;
  if (!weeks) return <Spinner />;

  // Anything a colleague has asked of this worker comes first: it is the only thing here that
  // someone else is waiting on.
  const toAnswer = requests.filter((r) => !r.mine && r.state === 'waiting_colleague');
  const mine = requests.filter((r) => r.mine);
  const openMine = mine.find((r) => r.id === openId);
  /** A week may be swapped only while it is published and has not started. */
  const swappable = (w: WeekInfo) => w.published && !!w.off_date && w.week_start > mondayOf(today);
  const thisWeek = mondayOf(today);
  // The last week the office has planned: next week, or the week after once it is published.
  const lastWeek = weeks[weeks.length - 1]?.week_start ?? thisWeek;
  const current = shown ?? thisWeek;
  const w = weeks.find((x) => x.week_start === current) ?? older[current] ?? null;
  const weekTitle = (start: string) => {
    const ahead = Math.round(
      (Date.parse(`${start}T00:00:00Z`) - Date.parse(`${thisWeek}T00:00:00Z`)) / (7 * 86_400_000),
    );
    return ahead < 0
      ? t('wo_past_week')
      : ahead === 0
        ? t('wo_this_week')
        : ahead === 1
          ? t('wo_next_week')
          : t('wo_week_after_next');
  };
  /** Any week up to the last one planned; the date field lands on that day's Monday. */
  const go = (start: string) => setShown(start > lastWeek ? lastWeek : start);

  return (
    <div className="stack">
      {knownAt && <OfflineNote at={knownAt} onRetry={() => void load()} />}
      {error && !knownAt && <Alert>{error}</Alert>}

      {/* A page title and one line, not a card of its own: on a phone the card cost a fifth of
          the first screen to say something an employee has read every week since June, and the
          week they came to look at was below the fold. */}
      <div className="page-intro">
        <h2 className="with-icon">
          <CalendarCheck size={20} aria-hidden="true" /> {t('wo_title')}
        </h2>
        <p className="muted small-text">{t('wo_sub')}</p>
      </div>

      {toAnswer.map((r) => (
        <section key={r.id} className="card swap-ask">
          <h3>{t('swap_asked_title', { name: r.worker.name })}</h3>
          <p>
            {t('swap_asked_body', {
              name: r.worker.name,
              theirs: `${dayName(r.to_date)}, ${fmtDate(r.to_date, false)}`,
              yours: `${dayName(r.from_date)}, ${fmtDate(r.from_date, false)}`,
            })}
          </p>
          {r.reason && <p className="muted">“{r.reason}”</p>}
          <div className="row">
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void act(() => post(`/me/off-swaps/${r.id}/respond`, { accept: true }))
              }
            >
              {t('swap_accept')}
            </button>
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() =>
                void act(() => post(`/me/off-swaps/${r.id}/respond`, { accept: false }))
              }
            >
              {t('swap_decline')}
            </button>
          </div>
        </section>
      ))}

      {/* One week at a time, any week back to the first, the way the admin page does it. The
          owner asked for employees to see their past weeks — view only, which they are: a week
          that has started can never be swapped. */}
      <span className="wo-weeks">
        <button
          type="button"
          className="small secondary wo-step"
          onClick={() => go(addDays(current, -7))}
          aria-label={t('wo_prev_week')}
          title={t('wo_prev_week')}
        >
          <ChevronLeft size={16} aria-hidden="true" />
        </button>
        <DateField
          className="wo-pick"
          label={t('wo_pick_week')}
          value={current}
          max={addDays(lastWeek, 6)}
          onChange={(ymd) => go(mondayOf(ymd))}
        />
        <button
          type="button"
          className="small secondary wo-step"
          onClick={() => go(addDays(current, 7))}
          disabled={current >= lastWeek}
          aria-label={t('wo_next_week_btn')}
          title={t('wo_next_week_btn')}
        >
          <ChevronRight size={16} aria-hidden="true" />
        </button>
        {current !== thisWeek && (
          <button type="button" className="small secondary" onClick={() => setShown(null)}>
            {t('wo_this_week')}
          </button>
        )}
      </span>
      {current < thisWeek && <p className="muted wo-past-note">{t('wo_past_note')}</p>}

      {!w ? (
        <Spinner />
      ) : (
        <>
          {/* The week as a card of its own colour, the off day the largest thing on it — the
            approved design of 15 October. Everyone's days follow in a plain card below. */}
          <section
            key={w.week_start}
            className={`card wo-week wo-hero ${w.published ? '' : 'unpublished'} ${current < thisWeek ? 'is-past' : ''}`}
          >
            <h3 className="wo-hero-kicker">
              {weekTitle(w.week_start)} · {fmtDate(w.week_start, false)} –{' '}
              {fmtDate(addDays(w.week_start, 6), false)}
            </h3>
            {w.published && w.off_date && (
              <strong className="wo-hero-day">
                {t('wo_off_on', { day: dayName(w.off_date) })} · {fmtDate(w.off_date, false)}
              </strong>
            )}
            <WeekChips
              weekStart={w.week_start}
              offDate={w.published ? w.off_date : null}
              today={today}
            />
            {!w.published ? (
              // A past week with no published plan is not "not published yet": it never will be.
              current < thisWeek ? (
                <p className="muted">{t('wo_past_none')}</p>
              ) : (
                <p className="muted with-icon">
                  <CalendarClock size={16} aria-hidden="true" /> {t('wo_not_published')}
                </p>
              )
            ) : w.off_date ? (
              swappable(w) &&
              (openMine ? (
                // Silently removing the button left a worker wondering what they had done wrong.
                <p className="muted small-text">{t('swap_one_at_a_time')}</p>
              ) : (
                <button type="button" className="secondary small" onClick={() => setAsking(w)}>
                  <Repeat size={15} aria-hidden="true" /> {t('swap_request')}
                </button>
              ))
            ) : (
              <p className="muted">{t('wo_no_off_this_week')}</p>
            )}
          </section>
          {w.published && w.days && w.days.length > 0 && (
            <section className="card wo-everyone">
              <WeekPlan days={w.days} meId={meId} today={today} />
            </section>
          )}
        </>
      )}

      {mine.length > 0 && (
        <section className="card">
          <h3>{t('swap_my_requests')}</h3>
          <ul className="list">
            {mine.map((r) => (
              <li key={r.id} className="swap-row">
                <div>
                  <strong>
                    {dayName(r.from_date)} → {dayName(r.to_date)}
                  </strong>{' '}
                  <span className={`badge ${stateTone(r.state)}`}>{stateLine(r)}</span>
                  <div className="muted small-text">
                    {fmtDate(r.from_date, false)} → {fmtDate(r.to_date, false)}
                    {r.counterparty ? ` · ${r.counterparty.name}` : ''}
                  </div>
                  {r.decision_note && <div className="muted small-text">“{r.decision_note}”</div>}
                </div>
                {r.id === openId && (
                  <button
                    type="button"
                    className="ghost small"
                    disabled={busy}
                    onClick={() => void act(() => post(`/me/off-swaps/${r.id}/cancel`))}
                  >
                    {t('swap_cancel')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {asking && (
        <AskForSwap
          week={asking}
          onClose={() => setAsking(null)}
          onDone={() => {
            setAsking(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

/** The seven days of a week as chips, the off day lit, today ringed. */
function WeekChips({
  weekStart,
  offDate,
  today,
}: {
  weekStart: string;
  offDate: string | null;
  today: string;
}) {
  return (
    <ol className="wo-strip" aria-label={t('wo_strip_label')}>
      {Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)).map((d) => {
        const off = d === offDate;
        return (
          <li
            key={d}
            className={`wo-chip ${off ? 'off' : ''} ${d === today ? 'today' : ''} ${d < today ? 'past' : ''}`}
            aria-current={d === today ? 'date' : undefined}
          >
            <span className="wd">{fmtWeekday(d).slice(0, 3)}</span>
            <span className="dn">{Number(d.slice(8, 10))}</span>
            <span className="tag">{off ? t('wo_off_short') : ' '}</span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The whole week, the way it is pinned to a wall: each day, and who is off on it.
 *
 * The owner asked for this so that a worker choosing whom to ask for a swap can see who is
 * already off on the day they want — which is precisely what the swap form then asks them for.
 * Their own row says "You", because a name among thirty names is hard to find and the one a
 * person is looking for first is their own.
 *
 * Shifts are marked only when the week has both, like everywhere else: a marker on every name
 * that never varies is noise.
 */
function WeekPlan({ days, meId, today }: { days: PlanDay[]; meId: string; today: string }) {
  const mixed = new Set(days.flatMap((d) => d.workers.map((w) => w.shift))).size > 1;
  return (
    <div className="wo-plan">
      <h4>{t('wo_everyone')}</h4>
      <table className="wo-plan-table">
        <thead>
          <tr>
            <th scope="col">{t('wo_col_day')}</th>
            <th scope="col">{t('wo_col_off')}</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d) => (
            <tr key={d.date} className={d.date === today ? 'is-today' : undefined}>
              <th scope="row">
                <span className="wo-plan-day">{fmtWeekday(d.date).slice(0, 3)}</span>{' '}
                <span className="wo-plan-date">{Number(d.date.slice(8, 10))}</span>
              </th>
              <td>
                {d.workers.length === 0 ? (
                  <span className="muted">{t('wo_nobody_off')}</span>
                ) : (
                  // One name to a line, which is what makes a column a column. Pills were solving
                  // a wrapping problem that a ruled table does not have.
                  <ul className="wo-plan-names">
                    {d.workers.map((w) => (
                      <li key={w.id} className={w.id === meId ? 'is-me' : undefined}>
                        {w.id === meId ? t('wo_you') : w.name}
                        {mixed && <small> {SHIFT_SHORT[w.shift]}</small>}
                      </li>
                    ))}
                  </ul>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Pick a day to move to, and optionally a colleague who is already off then. Naming one asks
 * them first; leaving it blank sends the request straight to an admin.
 */
function AskForSwap({
  week,
  onClose,
  onDone,
}: {
  week: WeekInfo;
  onClose: () => void;
  onDone: () => void;
}) {
  const [toDate, setToDate] = useState('');
  const [colleagues, setColleagues] = useState<{ id: string; name: string }[]>([]);
  const [counterparty, setCounterparty] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Any day of the week can be an off day, so all seven are offered: the week runs Monday
  // (i = 0) to Sunday (i = 6), less the day they already have.
  const days = Array.from({ length: 7 }, (_, i) => addDays(week.week_start, i)).filter(
    (d) => d !== week.off_date,
  );

  useEffect(() => {
    if (!toDate) {
      setColleagues([]);
      setCounterparty('');
      return;
    }
    api<{ colleagues: { id: string; name: string }[] }>(`/me/off-swaps/colleagues?date=${toDate}`)
      .then((r) => setColleagues(r.colleagues))
      .catch(() => setColleagues([]));
    setCounterparty('');
  }, [toDate]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await post('/me/off-swaps', {
        from_date: week.off_date,
        to_date: toDate,
        counterparty_id: counterparty || null,
        reason: reason.trim() || undefined,
      });
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SlideOver open title={t('swap_request')} onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {error && <Alert>{error}</Alert>}
        <p className="muted">
          {t('swap_current_day', {
            day: `${dayName(week.off_date!)}, ${fmtDate(week.off_date!, false)}`,
          })}
        </p>
        <label>
          {t('swap_pick_day')}
          <select value={toDate} onChange={(e) => setToDate(e.target.value)} required>
            <option value="">{t('swap_pick_day_placeholder')}</option>
            {days.map((d) => (
              <option key={d} value={d}>
                {dayName(d)} · {fmtDate(d, false)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('swap_pick_colleague')}
          <span className="muted block-help">{t('swap_pick_colleague_help')}</span>
          <select
            value={counterparty}
            onChange={(e) => setCounterparty(e.target.value)}
            disabled={!toDate}
          >
            <option value="">{t('swap_no_colleague')}</option>
            {colleagues.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {toDate && colleagues.length === 0 && (
            <span className="muted block-help">{t('swap_nobody_off')}</span>
          )}
        </label>
        <label>
          {t('swap_reason')}
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={300}
            placeholder={t('swap_reason_hint')}
          />
        </label>
        <div className="row">
          <button type="submit" disabled={busy || !toDate}>
            {t('swap_send')}
          </button>
          <button type="button" className="ghost" onClick={onClose} disabled={busy}>
            {t('cancel')}
          </button>
        </div>
      </form>
    </SlideOver>
  );
}
